import * as React from "react";
import {Link, useLocation} from 'react-router-dom';
import {
    GaugeIcon,
    MapPinIcon,
    RecycleIcon,
    TrendingDownIcon,
    ChartBarIcon,
    HomeIcon,
    Locate,
    Lightbulb,
} from "lucide-react";

import {NavMain} from "@/components/nav-main";
import {NavSecondary} from "@/components/nav-secondary";
import {NavUser} from "@/components/nav-user";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar,
} from "@/components/ui/sidebar";

import logo from "@/assets/svg/plaexLogo.svg";
import axiosInstance from "@/api/axios";
import ThemeToggle from "@/pages/contexts/ThemeTogglee";
import {useTranslation} from "react-i18next";


// Inner header content — reads sidebar state via useSidebar()
function AppSidebarHeader() {
    const {state} = useSidebar();
    const isCollapsed = state === "collapsed";

    return (
        <SidebarHeader>
            <SidebarMenu>
                <SidebarMenuItem>
                    {isCollapsed ? (
                        // When collapsed: show only the dark mode toggle, centered
                        <div className="flex items-center justify-center w-full h-10">
                            <ThemeToggle />
                        </div>
                    ) : (
                        // When expanded: show logo + name + dark mode toggle
                        <div className="flex items-center justify-between w-full pr-2">
                            <SidebarMenuButton
                                asChild
                                className="data-[slot=sidebar-menu-button]:!p-1.5 !h-12 flex items-center gap-2"
                            >
                                <Link to="/operations" className="flex items-center gap-2">
                                    <img src={logo} alt="logo" className="size-10 dark:invert"/>
                                    <span className="text-base font-semibold">PLAEX</span>
                                </Link>
                            </SidebarMenuButton>
                            <ThemeToggle />
                        </div>
                    )}
                </SidebarMenuItem>
            </SidebarMenu>
        </SidebarHeader>
    );
}


export function AppSidebar({userRole = "Basic", ...props}) {
    const {t} = useTranslation();
    const [userData, setUserData] = React.useState({
        name: "shadcn",
        email: "m@example.com",
        avatar: "/avatars/shadcn.jpg",
    });

    React.useEffect(() => {
        axiosInstance
            .get("")
            .then((response) => {
                setUserData({
                    name: response.data.company || "shadcn",
                    email: response.data.email || "m@example.com",
                    avatar: response.data.user_profile || "/avatars/shadcn.jpg",
                });
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    const getSidebarData = () => {
        const navItems = [
            {
                title: t("home"),
                url: "/homepage",
                icon: HomeIcon,
                visible: userRole === "Business"
            },
            {
                title: t("operations"),
                url: "/operations",
                icon: GaugeIcon,
                visible: true  // All roles can see this
            },
            {
                title: t("facility"),
                url: "/facility",
                icon: Locate,
                visible: userRole === "Business"
            },
            {
                title: t("locations"),
                url: "/locations",
                icon: MapPinIcon,
                visible: userRole === "Business"
            },
            {
                title: t("circularity"),
                url: "/circularity",
                icon: RecycleIcon,
                visible: userRole === "Mid" || userRole === "Business"
            },
            {
                title: t("costSavingstitle"),
                url: "/cost-savings",
                icon: TrendingDownIcon,
                visible: userRole === "Mid" || userRole === "Business"
            },
            {
                title: t("other"),
                url: "/other",
                icon: ChartBarIcon,
                visible: userRole === "Business"
            },
        ];

        const navSecondary = [
            {
                title: t("feedbacktitle"),
                url: "/feedback",
                icon: Lightbulb,
                visible: true  // Visible to all users
            }
        ];

        return {
            user: userData,
            navMain: navItems.filter(item => item.visible),
            navSecondary: navSecondary.filter(item => item.visible),
        };
    };

    const data = getSidebarData();

    return (
        // defaultOpen={false} makes it collapsed on first load.
        // The SidebarProvider in SidebarLayout.tsx also needs defaultOpen={false}
        // OR pass it here via the Sidebar component directly.
        <Sidebar collapsible="icon" {...props}>
            <AppSidebarHeader />

            <SidebarContent>
                <NavMain items={data.navMain}/>
                <NavSecondary items={data.navSecondary} className="mt-auto"/>
            </SidebarContent>

            <SidebarFooter className="relative">
                <NavUser user={data.user}/>
            </SidebarFooter>
        </Sidebar>
    );
}